import { type CSSProperties } from 'react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { useArcheAction } from '../../hooks/useArcheAction';
import { useArcheContext } from '../../hooks/useArcheContext';
import { cn } from '@/lib/utils';

interface ReviewItem {
  text: string;
  comment: string;
}

/**
 * ReviewList component - a read-only list of AI-generated items where the user
 * can add clarifying comments on any item.
 *
 * Items without comments are assumed correct/accepted. Item text is NOT editable -
 * only the comment field can be modified.
 */
export function ReviewList({ id, props, style }: SDUIComponentProps) {
  const { updateInput } = useArcheAction();
  const { context } = useArcheContext();

  const {
    name = 'reviewItems',
    label,
    disabled = false,
    commentPlaceholder = 'Add a clarification...',
    className,
  } = props as {
    name?: string;
    label?: string;
    disabled?: boolean;
    commentPlaceholder?: string;
    className?: string;
  };

  // Get current items from context
  const items = (context.input?.[name] as ReviewItem[]) || [];

  const updateComment = (index: number, comment: string) => {
    const updated = items.map((item, i) => (i === index ? { ...item, comment } : item));
    updateInput(name, updated);
  };

  return (
    <div id={id} className={cn('space-y-2', className)} style={style as CSSProperties}>
      {label && <label className="block text-sm font-medium text-foreground">{label}</label>}

      {items.length === 0 && (
        <p className="text-sm text-muted-foreground italic">No items to review.</p>
      )}

      <div className="space-y-3">
        {items.map((item, index) => (
          <div
            key={index}
            className={cn(
              'rounded-lg border border-border bg-card p-3',
              item.comment && 'border-primary/30',
            )}
          >
            {/* Read-only item text */}
            <div className="flex items-start gap-2 mb-2">
              <span className="text-muted-foreground font-mono text-sm select-none shrink-0 mt-0.5">
                {index + 1}.
              </span>
              <p className="text-sm text-foreground leading-relaxed">{item.text}</p>
            </div>

            {/* Comment field */}
            <div className="ml-6">
              <textarea
                value={item.comment || ''}
                onChange={(e) => updateComment(index, e.target.value)}
                placeholder={commentPlaceholder}
                disabled={disabled}
                rows={item.comment ? 2 : 1}
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground',
                  'placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                  'disabled:opacity-50',
                  'resize-none transition-all',
                  item.comment ? 'border-primary/30' : 'border-input',
                )}
                onFocus={(e) => {
                  if (!item.comment) {
                    (e.target as HTMLTextAreaElement).rows = 2;
                  }
                }}
                onBlur={(e) => {
                  if (!item.comment) {
                    (e.target as HTMLTextAreaElement).rows = 1;
                  }
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {items.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {items.filter((i) => i.comment).length} of {items.length} items have comments
        </p>
      )}
    </div>
  );
}
